import { useEffect, useMemo, useRef, useState } from 'react'
import { Loader2, Mountain, X } from 'lucide-react'

import { useAuth } from '../../auth/authContext'
import { ApiClient } from '../../data/api'
import { timeAgo } from '../../lib/format'
import { isDrive, routeState } from '../../lib/route'
import {
  belagBereich,
  belagGeprueft,
  belagLaenge,
  belagSatz,
  stufenName,
  stufenRat,
} from '../../lib/surface'
import type { Stage, SurfaceSection } from '../../lib/types'
import { fetchSurfaceReport, type SurfaceProgress } from './surfaceRun'

/**
 * Wo die Strecke unbefestigt ist - vor der Abfahrt und nicht am Schotterrand.
 *
 * Die Frage, die diese Maske beantwortet, ist eine einzige: fahre ich da durch
 * oder aussen herum? Das Fahrzeug dieser Reise ist ein VW T3 Camper, kein
 * Geländewagen, und der Unterschied zwischen einer geschotterten Nebenstrasse
 * und einer Piste mit 4wd_only entscheidet über einen Abend.
 *
 * Angezeigt wird deshalb ein Kilometerbereich und kein Warndreieck: „km 34–41,
 * 7.2 km Naturweg“ ist eine Auskunft, mit der sich planen lässt. Daneben stehen
 * die rohen OSM-Merkmale, auf denen die Einstufung beruht - wer der Zahl nicht
 * traut, soll nachsehen können, woher sie kommt.
 *
 * Geholt wird auf Knopfdruck und nicht beim Berechnen der Strecke. Der Grund
 * ist die Quelle: der Server schlägt den Belag bei Overpass nach, das dauert
 * Sekunden bis Minuten und scheitert regelmässig. Eine Routenberechnung, die
 * daran hängenbliebe, wäre der schlechteste Tausch - die Strecke ist der Zweck,
 * der Belag die Beigabe. Was einmal geholt wurde, liegt danach in der Etappe
 * und ist unterwegs ohne Netz da.
 */
export function StageSurfacePanel({
  stage,
  onChange,
}: {
  stage: Stage
  onChange: (patch: Partial<Stage>) => void
}) {
  const { getToken } = useAuth()
  const client = useMemo(() => new ApiClient(getToken), [getToken])
  const [laeuft, setLaeuft] = useState(false)
  const [fortschritt, setFortschritt] = useState<SurfaceProgress | null>(null)
  const [fehler, setFehler] = useState('')
  const abbruch = useRef<AbortController | null>(null)

  // Wer die Etappe zuklappt, während der Abruf läuft, hat es sich anders
  // überlegt. Ohne den Abbruch schriebe die Antwort danach noch in eine Etappe,
  // die niemand mehr ansieht - und sie liefe dabei noch minutenlang weiter.
  useEffect(() => () => abbruch.current?.abort(), [])

  const route = stage.route
  const zustand = routeState(stage)
  const bericht = route?.surface

  if (!isDrive(stage)) return null

  async function holen() {
    if (!route) return
    abbruch.current?.abort()
    const steuer = new AbortController()
    abbruch.current = steuer

    setLaeuft(true)
    setFehler('')
    setFortschritt(null)
    try {
      const ergebnis = await fetchSurfaceReport(route.from, route.via ?? [], route.to, {
        fetchSurface: (from, via, to, signal) => client.fetchSurface(from, via, to, signal),
        onProgress: setFortschritt,
        signal: steuer.signal,
      })
      if (steuer.signal.aborted) return
      if (ergebnis.error) setFehler(ergebnis.error)
      // Auch ein Teilergebnis wird gespeichert: es sagt ehrlich, auf wie viel
      // Prozent nachgesehen wurde, und beim nächsten Druck kommt der schon
      // geholte Teil aus dem Speicher des Servers und kostet nichts mehr.
      // Nichts zu speichern hiesse, die Arbeit wegzuwerfen.
      if (ergebnis.report) {
        onChange({ route: { ...route, surface: ergebnis.report } })
      }
    } catch {
      if (!steuer.signal.aborted) setFehler('Der Belag liess sich nicht nachschlagen.')
    } finally {
      if (!steuer.signal.aborted) {
        setLaeuft(false)
        setFortschritt(null)
      }
    }
  }

  return (
    <section className="inhalt-block belag-block">
      <h5 className="block-titel">
        <Mountain size={13} /> Belag der Strecke
      </h5>

      {zustand !== 'current' ? (
        <p className="leer">
          {zustand === 'none'
            ? 'Der Belag hängt an der berechneten Strecke. Erst die Strecke dieser Etappe berechnen lassen.'
            : 'Die gespeicherte Strecke passt nicht mehr zu Start, Ziel oder Zwischenpunkten. Erst neu berechnen lassen.'}
        </p>
      ) : (
        <>
          <p className={belagWarnung(bericht?.sections) ? 'karte-warnung' : 'abschluss'}>
            {belagSatz(bericht)}
          </p>

          {bericht && bericht.sections.length > 0 && (
            <ul className="belagliste">
              {bericht.sections.map((abschnitt, i) => (
                <li key={`${String(abschnitt.fromKm)}-${abschnitt.grade}-${String(i)}`}>
                  <span className="belag-km">{belagBereich(abschnitt)}</span>
                  <span>
                    <strong>{stufenName(abschnitt.grade)}</strong> {belagLaenge(abschnitt.lengthM)}
                    {abschnitt.ref || abschnitt.name ? ` · ${abschnitt.ref ?? abschnitt.name}` : ''}
                    <small>{stufenRat(abschnitt.grade)}</small>
                    {/*
                      Die rohen Merkmale, unverändert in der Schreibweise von
                      OpenStreetMap. Sie stehen hier und nicht im Buch: am
                      Bildschirm ist Platz, und wer der Einstufung nicht traut,
                      sieht hier, worauf sie beruht. Im fahrenden Auto liest
                      „track/gravel/grade2/horrible“ dagegen niemand.
                    */}
                    <small className="belag-merkmale">{merkmalsText(abschnitt)}</small>
                  </span>
                </li>
              ))}
            </ul>
          )}

          <div className="knopfreihe">
            <button
              type="button"
              className={bericht ? 'knopf still' : 'knopf'}
              disabled={laeuft}
              onClick={() => void holen()}
            >
              {laeuft ? <Loader2 size={14} className="dreht" /> : <Mountain size={14} />}
              {laeuft
                ? fortschritt && fortschritt.blocks > 0
                  ? `Sucht in OpenStreetMap … ${String(fortschritt.done)}/${String(fortschritt.blocks)}`
                  : 'Sucht in OpenStreetMap …'
                : bericht
                  ? 'Belag neu nachschlagen'
                  : 'Belag nachschlagen'}
            </button>
            {laeuft && (
              <button
                type="button"
                className="knopf still"
                onClick={() => abbruch.current?.abort()}
              >
                <X size={14} /> Abbrechen
              </button>
            )}
          </div>

          {fehler && <p className="karte-warnung">{fehler}</p>}

          {bericht && (
            <p className="abschluss">
              Nachgeschlagen {timeAgo(bericht.checkedAt)}. Belag aus OpenStreetMap, ODbL — er ist so
              gut wie die Kartenpflege vor Ort, und im Buch bleibt daneben Platz zum Berichtigen.
              {!belagGeprueft(bericht) &&
                ' Die Abdeckung bleibt unter vier Fünfteln: der Routendienst rechnet auf einem älteren Kartenabzug, und was er nicht mehr auflösen kann, bleibt unbekannt.'}
            </p>
          )}
        </>
      )}
    </section>
  )
}

/**
 * Ist unter den Abschnitten einer, vor dem gewarnt werden muss?
 *
 * Nur ab „grob“. Eine geschotterte Nebenstrasse ist eine Auskunft und keine
 * Warnung; sie rot zu setzen hiesse, auf halber Strecke durch Südosteuropa
 * dauernd Alarm zu schlagen, und danach liest niemand mehr hin.
 */
function belagWarnung(sections: readonly SurfaceSection[] | undefined): boolean {
  return (sections ?? []).some((a) => a.grade === 'grob' || a.grade === 'gelaende')
}

/**
 * Die rohen OSM-Merkmale eines Abschnitts, so wie sie in der Karte stehen.
 *
 * Nur, was wirklich dasteht: ein „surface=—“ für ein fehlendes Merkmal wäre
 * eine Angabe, die niemand gemacht hat. Ist gar nichts gesetzt, entschied die
 * Strassenklasse allein, und genau das steht dann da.
 */
function merkmalsText(a: SurfaceSection): string {
  const teile = [
    a.surface && `surface=${a.surface}`,
    a.tracktype && `tracktype=${a.tracktype}`,
    a.smoothness && `smoothness=${a.smoothness}`,
    a.fourwd && `4wd_only=${a.fourwd}`,
  ].filter((t): t is string => Boolean(t))

  const klasse = a.highway ? `highway=${a.highway}` : ''
  if (teile.length === 0) {
    return klasse ? `${klasse} — ohne Belagsangabe, eingestuft nach der Strassenklasse` : ''
  }
  return [klasse, ...teile].filter(Boolean).join(', ')
}
